import { useTranslation } from 'react-i18next';
import { UseFormRegister, Path } from 'react-hook-form';
import { HeadcountRange, IndustrySector, type SaveCompanyDto } from '@monorepo/shared';
import { FormInput } from '../ui/FormInput';
import { FormSelect } from '../ui/FormSelect';
import { VALIDATION_PATTERNS } from '../../constants/validation';
import { useAuthStore } from '../../stores/auth.store';

interface CompanyInfoErrors {
  siren?: { message?: string };
  legalName?: { message?: string };
  industrySector?: { message?: string };
  industrySectorOther?: { message?: string };
  headcountRange?: { message?: string };
  mainWebsiteUrl?: { message?: string };
}

interface CompanyInfoFormProps<T extends SaveCompanyDto> {
  register: UseFormRegister<T>;
  errors: CompanyInfoErrors;
  industrySector?: IndustrySector;
  currentSiren?: string;
  onRequestSirenUpdate?: () => void;
}

export const CompanyInfoForm = <T extends SaveCompanyDto>({
  register,
  errors,
  industrySector,
  currentSiren,
  onRequestSirenUpdate,
}: CompanyInfoFormProps<T>) => {
  const { t } = useTranslation();
  const isUserMainContact = useAuthStore((state) => state.user?.isMainContact);

  const industrySectorOptions = [
    { value: IndustrySector.TOWN_HALL, label: t('company.industry_sector.values.TOWN_HALL') },
    {
      value: IndustrySector.COMMUNITY_OF_COMMUNES,
      label: t('company.industry_sector.values.COMMUNITY_OF_COMMUNES'),
    },
    { value: IndustrySector.INSURANCE, label: t('company.industry_sector.values.INSURANCE') },
    {
      value: IndustrySector.HOSPITAL_SECTOR,
      label: t('company.industry_sector.values.HOSPITAL_SECTOR'),
    },
    { value: IndustrySector.SCHOOL, label: t('company.industry_sector.values.SCHOOL') },
    { value: IndustrySector.BANK, label: t('company.industry_sector.values.BANK') },
    { value: IndustrySector.PRIVATE, label: t('company.industry_sector.values.PRIVATE') },
    { value: IndustrySector.OTHER, label: t('company.industry_sector.values.OTHER') },
  ];

  const headcountOptions = [
    { value: HeadcountRange.SMALL, label: t('company.headcount_range.small') },
    { value: HeadcountRange.MEDIUM, label: t('company.headcount_range.medium') },
    { value: HeadcountRange.LARGE, label: t('company.headcount_range.large') },
    { value: HeadcountRange.VERY_LARGE, label: t('company.headcount_range.very_large') },
    { value: HeadcountRange.EXTRA_LARGE, label: t('company.headcount_range.extra_large') },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-text border-b pb-2">
        {t('company.section.company_info')}
      </h2>

      {!currentSiren && (
        <FormInput
          label={t('company.siren.label')}
          type="text"
          placeholder={t('company.siren.placeholder')}
          error={'siren' in errors ? (errors as any).siren?.message : undefined}
          required
          {...register('siren' as Path<T>, {
            required: t('company.siren.error.required'),
            setValueAs: (value) => value?.replace(/\s/g, ''),
            pattern: {
              value: VALIDATION_PATTERNS.SIREN,
              message: t('company.siren.error.invalid'),
            },
          })}
        />
      )}

      {currentSiren && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('company.siren.label')}
          </label>
          <input
            type="text"
            value={currentSiren}
            disabled
            className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed mb-2"
          />
          {onRequestSirenUpdate && isUserMainContact && (
            <button
              type="button"
              onClick={onRequestSirenUpdate}
              className="text-sm text-primary hover:text-opacity-80 transition font-medium"
            >
              {t('company.siren.update_button')}
            </button>
          )}
        </div>
      )}

      <FormInput
        label={t('company.legal_name.label')}
        type="text"
        placeholder={t('company.legal_name.placeholder')}
        error={errors.legalName?.message}
        required
        {...register('legalName' as Path<T>, {
          required: t('company.legal_name.error.required'),
        })}
      />

      <FormSelect
        label={t('company.industry_sector.label')}
        options={industrySectorOptions}
        placeholder={t('company.industry_sector.placeholder')}
        error={errors.industrySector?.message}
        required
        {...register('industrySector' as Path<T>, {
          required: t('company.industry_sector.error.required'),
        })}
      />

      {industrySector === IndustrySector.OTHER && (
        <FormInput
          label={t('company.industry_sector_other.label')}
          type="text"
          placeholder={t('company.industry_sector_other.placeholder')}
          error={errors.industrySectorOther?.message}
          required
          {...register('industrySectorOther' as Path<T>, {
            required: t('company.industry_sector_other.error.required'),
          })}
        />
      )}

      <FormSelect
        label={t('company.headcount_range.label')}
        options={headcountOptions}
        placeholder={t('company.headcount_range.placeholder')}
        error={errors.headcountRange?.message}
        required
        {...register('headcountRange' as Path<T>, {
          required: t('company.headcount_range.error.required'),
        })}
      />

      <FormInput
        label={t('company.main_website_url.label')}
        type="url"
        placeholder={t('company.main_website_url.placeholder')}
        error={errors.mainWebsiteUrl?.message}
        {...register('mainWebsiteUrl' as Path<T>, {
          validate: (value) =>
            !value ||
            VALIDATION_PATTERNS.URL.test(value) ||
            t('company.main_website_url.error.invalid'),
        })}
      />
    </div>
  );
};
